import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const MM_PER_INCH = 25.4;

export const RULES = [
  { value: "equal", label: "Equal" },
  { value: "weighted_bottom", label: "Weighted Bottom" },
  { value: "golden", label: "Golden Ratio" },
  { value: "custom", label: "Custom" },
];

export const RULE_NOTES = {
  equal: "Equal mat borders on all four sides — clean and neutral.",
  weighted_bottom: "Bottom border enlarged by the weighting factor — the classic gallery look that visually balances the artwork.",
  golden: "Bottom border = side border × φ (1.618) for a naturally pleasing proportion.",
  custom: "Set each border independently for full control.",
};

export const LAYER_OPTS = [
  { value: "0", label: "None" },
  { value: "1", label: "Single" },
  { value: "2", label: "Double" },
  { value: "3", label: "Triple" },
];

export const MOULDING_COLORS = [
  { name: "Natural Oak", hex: "#C9A66B" },
  { name: "Walnut", hex: "#5C4433" },
  { name: "Espresso", hex: "#3B2C24" },
  { name: "Black", hex: "#1C1C1C" },
  { name: "White", hex: "#F5F5F0" },
  { name: "Gold", hex: "#C4A032" },
  { name: "Silver", hex: "#B8BCC0" },
  { name: "Slate", hex: "#5A6470" },
  { name: "Lime Wash Lime", hex: "#F2F0E9" },
];

export const MAT_COLORS_OPTS = [
  { name: "Bright White", hex: "#FBFBF8" },
  { name: "GoldenRod", hex: "#DAA520" },
  { name: "Wattle", hex: "#E0C34F" },
  { name: "Chinese Red", hex: "#C7352B" },
  { name: "Ruby", hex: "#9B111E" },
  { name: "Red", hex: "#C0392B" },
  { name: "African Violet", hex: "#9E7BB5" },
  { name: "Maroon", hex: "#7B2E3A" },
  { name: "Burgundy", hex: "#6E1E2A" },
  { name: "Antique Rose", hex: "#C9A0A0" },
  { name: "Blush", hex: "#E7C9C2" },
  { name: "Feather Grey", hex: "#D8D8D4" },
  { name: "Soft Mauve", hex: "#C9B3C0" },
  { name: "Lavender", hex: "#B9A6D4" },
  { name: "Grey Violet", hex: "#8E8698" },
  { name: "Charcoal Grey", hex: "#3A3A3A" },
  { name: "Olive", hex: "#6B6B3A" },
  { name: "Balsam", hex: "#4C6B57" },
  { name: "Antique", hex: "#E6DCC3" },
  { name: "Thistle", hex: "#C9AEC9" },
  { name: "Lichen", hex: "#A6B58A" },
  { name: "Aqua Green", hex: "#7FC8B8" },
  { name: "Silver Pine", hex: "#6E7F76" },
  { name: "Medium Blue", hex: "#4A6DA7" },
  { name: "Peregrine", hex: "#6E7A86" },
  { name: "Royal Blue", hex: "#2B4B9B" },
  { name: "Caribbean Blue", hex: "#2FA5C4" },
  { name: "Azure", hex: "#3A7CC4" },
  { name: "Venetian Blue", hex: "#3E5C8A" },
  { name: "Fathom", hex: "#2A3E52" },
  { name: "Pacific Blue", hex: "#1F6F94" },
  { name: "Riviera Blue", hex: "#2E86AB" },
  { name: "Antique Blue", hex: "#7C93A8" },
  { name: "Limoges", hex: "#3E4C7A" },
];

const inchStr = (mm) => `${(mm / MM_PER_INCH).toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}″`;
const mkOpts = (arr) => arr.map((mm) => ({ mm, label: mm === 0 ? "None" : `${mm} mm (${inchStr(mm)})` }));

export const GLASS_OPTS = mkOpts([0, 2, 3, 4, 4.5, 6]);
export const PLY_OPTS = [
  { mm: 0, label: "None" },
  { mm: 0.8, label: "2-ply thin (0.8 mm / 0.031″)" },
  { mm: 1.4, label: "4-ply standard (1.4 mm / 0.055″)" },
  { mm: 2.0, label: "6-ply prominent (2.0 mm / 0.079″)" },
  { mm: 3.1, label: "8-ply museum (3.1 mm / 0.122″)" },
];
export const BACKING_OPTS = mkOpts([0, 1.6, 2, 3, 3.2, 5, 10]);
export const SPACER_OPTS = mkOpts([0, 1.5, 3, 5, 6]);

export const SPACER_TYPE_OPTS = [
  { value: "none", label: "None" },
  { value: "matboard", label: "Matboard" },
  { value: "foam", label: "Foam board" },
  { value: "plastic", label: "Plastic strips" },
  { value: "wood", label: "Wood" },
];

export const SPACER_DEPTH_OPTS = {
  matboard: [
    { mm: 0.8, label: `2-ply (0.8 mm / ${inchStr(0.8)})` },
    { mm: 1.4, label: `4-ply (1.4 mm / ${inchStr(1.4)})` },
    { mm: 2.0, label: `6-ply (2.0 mm / ${inchStr(2.0)})` },
    { mm: 3.1, label: `8-ply (3.1 mm / ${inchStr(3.1)})` },
  ],
  foam: mkOpts([3, 5, 10]).filter((o) => o.mm > 0),
  plastic: mkOpts([1.6, 3, 6.5, 10]).filter((o) => o.mm > 0),
  wood: mkOpts([3, 6, 12, 20]).filter((o) => o.mm > 0),
};

// ---------------- Costing ----------------
const IN = 25.4;
export const SHEET_UNITS = {
  "32x40": { label: '32" × 40" sheet', w: 32 * IN, h: 40 * IN },
  "36x48": { label: '36" × 48" sheet', w: 36 * IN, h: 48 * IN },
  "40x60": { label: '40" × 60" sheet', w: 40 * IN, h: 60 * IN },
  "48x68": { label: '48" × 68" sheet', w: 48 * IN, h: 68 * IN },
  "48x96": { label: '48" × 96" sheet', w: 48 * IN, h: 96 * IN },
  A1: { label: "A1 (594 × 841 mm)", w: 594, h: 841 },
  A2: { label: "A2 (420 × 594 mm)", w: 420, h: 594 },
  A3: { label: "A3 (297 × 420 mm)", w: 297, h: 420 },
  A4: { label: "A4 (210 × 297 mm)", w: 210, h: 297 },
};
export const LINEAR_UNITS = {
  m: { label: "per linear metre", per_mm: 1000 },
  ft: { label: "per linear foot", per_mm: 304.8 },
};
export const CUSTOM_UNIT = { custom: { label: "Custom area…" } };

export const COST_UNIT_OPTS = {
  matboard: ["32x40", "40x60"],
  moulding: ["m", "ft"],
  glazing: ["32x40", "36x48", "40x60", "48x68", "custom"],
  spacer_matboard: ["32x40", "40x60"],
  spacer_foam: ["32x40", "40x60", "48x96", "A1", "A2", "A3", "A4"],
  spacer_plastic: ["m", "ft"],
  spacer_wood: ["m", "ft"],
};

export const unitLabel = (key) =>
  (SHEET_UNITS[key] && SHEET_UNITS[key].label) ||
  (LINEAR_UNITS[key] && LINEAR_UNITS[key].label) ||
  (key === "custom" ? "Custom area…" : "");

const toMm = (v, unit) => (unit === "in" ? (v || 0) * IN : (v || 0));
const num = (v) => parseFloat(v) || 0;

// Pure cost calculator. Returns { rows:[{key,label,cost,detail}], total, currency }
export function computeCosts(form, r, unit) {
  const currency = form.currency || "$";
  if (!r) return { rows: [], total: 0, currency };
  const rows = [];

  const areaCost = (usedW, usedH, sheetW, sheetH, price) => {
    const sheetArea = sheetW * sheetH;
    if (!price || sheetArea <= 0) return null;
    return (toMm(usedW, unit) * toMm(usedH, unit)) / sheetArea * price;
  };
  const linearCost = (lenMm, unitKey, price) => {
    const lu = LINEAR_UNITS[unitKey];
    if (!lu || !price) return null;
    return (lenMm / lu.per_mm) * price;
  };
  const sheetCost = (usedW, usedH, unitKey, price) => {
    const s = SHEET_UNITS[unitKey];
    if (!s) return null;
    return areaCost(usedW, usedH, s.w, s.h, price);
  };

  // Matboard (area × number of mat layers)
  if (r.total_mat_width > 0) {
    const nLayers = parseInt(form.mat_layers, 10) || 1;
    const per = sheetCost(r.total_mat_width, r.total_mat_height, form.mat_cost_unit, num(form.mat_cost_price));
    if (per != null) rows.push({ key: "matboard", label: nLayers > 1 ? `Matboard (×${nLayers})` : "Matboard", cost: per * nLayers });
  }
  // Moulding (linear)
  {
    const c = linearCost(toMm(r.moulding_length_total, unit), form.moulding_cost_unit, num(form.moulding_cost_price));
    if (c != null) rows.push({ key: "moulding", label: "Moulding", cost: c });
  }
  // Glazing (area, or custom area)
  {
    const price = num(form.glazing_cost_price);
    let c = null;
    if (form.glazing_cost_unit === "custom") {
      c = areaCost(r.glass_cut_width, r.glass_cut_height, toMm(num(form.glazing_custom_w), unit), toMm(num(form.glazing_custom_h), unit), price);
    } else {
      c = sheetCost(r.glass_cut_width, r.glass_cut_height, form.glazing_cost_unit, price);
    }
    if (c != null) rows.push({ key: "glazing", label: "Glazing", cost: c });
  }
  // Spacer (units depend on type)
  if (r.spacer_type && r.spacer_type !== "none") {
    const price = num(form.spacer_cost_price);
    const u = form.spacer_cost_unit;
    let c = null;
    if (u === "m" || u === "ft") {
      const lenMm = r.spacer_type === "wood"
        ? 2 * toMm(r.spacer_wood_length_a, unit) + 2 * toMm(r.spacer_wood_length_b, unit)
        : 2 * toMm(r.spacer_tb_length, unit) + 2 * toMm(r.spacer_lr_length, unit);
      c = linearCost(lenMm, u, price);
    } else {
      c = sheetCost(r.spacer_outer_width, r.spacer_outer_height, u, price);
    }
    if (c != null) rows.push({ key: "spacer", label: "Spacer", cost: c });
  }

  const total = rows.reduce((s, x) => s + x.cost, 0);
  return { rows, total, currency };
}

export const fmtMoney = (v, currency) => `${currency || "$"}${(v || 0).toFixed(2)}`;
export const PAPER_OPTS = [
  { mm: 0, label: "Not set" },
  { mm: 0.25, label: "Lightweight — 185 gsm (0.25 mm / 0.010″)" },
  { mm: 0.35, label: "Standard — 300 gsm (0.35 mm / 0.014″)" },
  { mm: 0.5, label: "Heavy — 400 gsm (0.5 mm / 0.020″)" },
  { mm: 0.6, label: "Extra-heavy — 600 gsm (0.6 mm / 0.024″)" },
];

export const HELP = {
  reference: "A name for this job or client. It fills the 'Save as' name and prints on the cut sheet header.",
  mode: "Choose what you know. 'Frame size' works forward from the artwork; 'Artwork size' works backward from a known outer frame.",
  painting: "The artwork's width and height. Depth is the physical thickness (e.g. a stretched canvas or the paper), used only in the depth calculation.",
  mat: "Pick how many mat layers and the aesthetic rule. Under-mat reveals are the slivers of each lower mat that show inside the top mat window.",
  moulding: "Face width is the visible width of the wood. Rebate/overlap is how far the lip covers the contents. Rebate depth is how deep the recess is (used for the depth check).",
  depth: "Adds up the thickness of every layer (glazing, spacer, mats, artwork, backing) and warns you if the stack is deeper than the moulding rebate.",
};

export const PRESETS = [
  { group: "A-series (mm)", items: [
    { label: "A5 — 148 × 210", w: 148, h: 210 },
    { label: "A4 — 210 × 297", w: 210, h: 297 },
    { label: "A3 — 297 × 420", w: 297, h: 420 },
    { label: "A2 — 420 × 594", w: 420, h: 594 },
    { label: "A1 — 594 × 841", w: 594, h: 841 },
  ]},
  { group: "Photo / print (in)", items: [
    { label: "5 × 7 in", w: 127, h: 177.8 },
    { label: "8 × 10 in", w: 203.2, h: 254 },
    { label: "11 × 14 in", w: 279.4, h: 355.6 },
    { label: "16 × 20 in", w: 406.4, h: 508 },
    { label: "18 × 24 in", w: 457.2, h: 609.6 },
    { label: "24 × 36 in", w: 609.6, h: 914.4 },
  ]},
  { group: "Square (mm)", items: [
    { label: "200 × 200", w: 200, h: 200 },
    { label: "300 × 300", w: 300, h: 300 },
    { label: "500 × 500", w: 500, h: 500 },
  ]},
];

export function fmt(value, unit) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const decimals = unit === "in" ? 3 : 1;
  return Number(value).toFixed(decimals);
}

export function convertVal(v, from, to) {
  if (from === to) return v;
  const n = parseFloat(v);
  if (Number.isNaN(n)) return v;
  const mm = from === "in" ? n * MM_PER_INCH : n;
  const out = to === "in" ? mm / MM_PER_INCH : mm;
  return parseFloat(out.toFixed(to === "in" ? 3 : 1));
}

export async function calculate(input) {
  const { data } = await axios.post(`${API}/calculate`, input);
  return data;
}
